import React, { FC, useMemo, useRef, useState } from 'react';
import Dialog, { DialogProps } from '../../../../components/dialog/Dialog';
import classNames from 'classnames';
import { useEditor } from '@mnz/builder';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SiteThemeDataFragment, useEditSiteDesignMutation } from '../../../../generated/types';

interface BrowseThemeModalProps extends DialogProps {
    themes: SiteThemeDataFragment[];
    currentThemeId: number | null;
}

const BrowseThemeModal: FC<BrowseThemeModalProps> = ({ themes, currentThemeId, ...props }) => {
    const abortController = useRef<AbortController>();
    const [saveDraftDesign] = useEditSiteDesignMutation();
    const [themeId, setThemeId] = useState(currentThemeId);
    const {
        actions: { deserialize },
        account,
        elements,
    } = useEditor((_, query) => ({
        account: query.getAccountInfo(),
        elements: query.serialize(),
    }));

    const handleSelectTheme = async (theme: SiteThemeDataFragment) => {
        deserialize(elements, theme.designSettings, account);
        if (abortController.current) {
            abortController.current.abort();
        }
        abortController.current = new window.AbortController();
        setThemeId(theme.id);
        await saveDraftDesign({
            variables: {
                input: {
                    patch: {
                        themeId: theme.id,
                    },
                },
            },
            context: { fetchOptions: { signal: abortController.current.signal } },
        });
    };

    const initSlide = useMemo(() => {
        if (themeId) {
            return themes.findIndex((theme) => theme.id === themeId);
        } else {
            return 0;
        }
    }, [themeId, themes]);

    return (
        <Dialog {...props} anchor="bottom" title="Choose Template">
            <div className={`mt-20 pb-40`}>
                <Swiper initialSlide={initSlide} slidesPerView={'auto'} observer={true}>
                    {themes.map((theme, i) => (
                        <SwiperSlide
                            key={i}
                            className={classNames({
                                '!w-100 !h-auto border-3 rounded-8 p-3': true,
                                'ml-28 mr-4': i === 0,
                                'ml-4 mr-28': i === themes.length - 1,
                                'mx-4': 0 < i && i < themes.length - 1,
                                'border-success-60': themeId === theme.id,
                                'border-transparent': themeId !== theme.id,
                            })}
                            onClick={() => handleSelectTheme(theme)}
                        >
                            <img src={theme.image} className={'rounded-6'} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </Dialog>
    );
};

export default BrowseThemeModal;
